import React from 'react';

/**
 * Pagination — compact, responsive pagination UI.
 * Props:
 * - page: current 0-based index
 * - totalPages: number of pages
 * - onPageChange(page)
 * - pageSize, onPageSizeChange(size), pageSizeOptions
 * - totalItems: optional number to show "X of Y"
 */
export const Pagination = ({
  page = 0,
  totalPages = 0,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 25, 50],
  totalItems,
  className = '',
}) => {
  if (totalPages <= 0 && !onPageSizeChange) return null;

  const goTo = (next) => {
    if (!onPageChange) return;
    const clamped = Math.max(0, Math.min(totalPages - 1, next));
    if (clamped !== page) onPageChange(clamped);
  };

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-1 mt-4 ${className}`}>
      <div className="flex items-center gap-4 text-sm text-slate-600">
        {totalPages > 0 && (
          <span>
            Page <span className="font-semibold text-slate-900">{page + 1}</span>{' '}
            of <span className="font-semibold text-slate-900">{totalPages}</span>
            {typeof totalItems === 'number' && (
              <span className="ml-2 text-slate-400">· {totalItems} records</span>
            )}
          </span>
        )}
        {onPageSizeChange && (
          <label className="inline-flex items-center gap-2">
            <span className="text-slate-500">Rows:</span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="appearance-none pl-3 pr-8 py-1.5 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-300"
              >
                {pageSizeOptions.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </label>
        )}
      </div>

      {totalPages > 0 && (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => goTo(0)}
            disabled={page === 0}
            className="icon-action border border-slate-200 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="First page"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            className="inline-flex items-center gap-1 px-3 h-8 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Prev</span>
          </button>
          <button
            type="button"
            onClick={() => goTo(page + 1)}
            disabled={page >= totalPages - 1}
            className="inline-flex items-center gap-1 px-3 h-8 text-sm rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline">Next</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => goTo(totalPages - 1)}
            disabled={page >= totalPages - 1}
            className="icon-action border border-slate-200 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Last page"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
